import React, { useCallback, useState } from 'react';
import * as PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import S from './group-header.module.scss';
import { GroupName } from './group-name';
import { GroupPageSubHeader } from './group-sub-header';
import { BtnGroupDelete } from '../group-actions/button-group-delete';
import { BtnGroupBlast } from '../group-actions/button-group-blast';
import { BtnGroupInvite } from '../group-actions/button-group-invite';
import { SearchInput } from '../../../../../common/search-input';
import { useHasPermission } from 'graphql/graph-hooks';
import { useDebounce } from '../../../../../../helpers/hooks/use-debounce';
import { useModal } from '../../../../../global-hooks';
import { PERMISSIONS, UI_MODALS } from '../../../../../../helpers/enums';

const GroupHeader = ({name, groupId, dancers, isDefaultGroup, onFilterChange, filterValue, onDancersRemove, selectedDancers, selectDancers, deselectDancers }) => {
  const CAN_SEND_INVITES = useHasPermission(PERMISSIONS.SEND_INVITES) && selectedDancers.length > 0;
  const { t } = useTranslation();

  const [searchInputValue, setSearchInputValue] = useState(filterValue);

  const { onChange: debouncedOnChange } = useDebounce(searchInputValue, onFilterChange);

  const onSearchInputChange = async (e) => {
    setSearchInputValue(e.target.value);
    debouncedOnChange(e);
  };

  const { initModal } = useModal();

  const handleInvite = useCallback(
    () =>
      initModal(UI_MODALS.INVITE_DANCER, {
        dancers: dancers.filter(({ id }) => selectedDancers.includes(id)),
      }),
    [initModal, dancers, selectedDancers]
  );

  return (
    <header className={S.groupDetailHeader}>
      <GroupName groupId={groupId} name={name} />

      <div className={S.groupDetailActionRow}>
        <BtnGroupInvite disabled={!CAN_SEND_INVITES} handleInvite={handleInvite} />
        <BtnGroupBlast groupId={groupId} />
        <BtnGroupDelete groupId={groupId} disabled={isDefaultGroup} />
      </div>

      <GroupPageSubHeader
        groupId={groupId}
        dancers={dancers}
        onDancersRemove={onDancersRemove}
        selectedDancers={selectedDancers}
        selectDancers={selectDancers}
        deselectDancers={deselectDancers}
      />
      <SearchInput gridArea='filter' placeholder={t('common.search_dancers')} value={searchInputValue}  onChange={onSearchInputChange} />
    </header>
  );
};

GroupHeader.displayName = 'GroupHeader';

GroupHeader.propTypes = {
  groupId: PropTypes.string.isRequired,
  dancers: PropTypes.array.isRequired,
  name: PropTypes.string.isRequired,
  isDefaultGroup: PropTypes.bool,
  onFilterChange: PropTypes.func.isRequired,
  filterValue: PropTypes.string,
  onDancersRemove: PropTypes.func,
  selectedDancers: PropTypes.arrayOf(PropTypes.string),
  selectDancers: PropTypes.func.isRequired,
  deselectDancers: PropTypes.func.isRequired,
};

export { GroupHeader };
